/**
 * MarketNewsList Component
 *
 * A simple vertical list of market news items.
 * No carousel, no dismiss, no stack - just a clean list.
 */

import { FC } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { colors, spacing, fontStyles } from '../../styles';
import { PortfolioNewsArticle } from '../../apis/backend';
import { toSentenceCase } from '../../utils/formatDisplay';

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function safeParseDate(dateString: string | undefined | null): Date {
  if (!dateString) return new Date();
  const parsed = new Date(dateString);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

interface MarketNewsListProps {
  newsArticles: PortfolioNewsArticle[];
  isLoading: boolean;
  maxItems?: number;
}

const MarketNewsList: FC<MarketNewsListProps> = ({ newsArticles, isLoading, maxItems = 5 }) => {
  const history = useHistory();
  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="market-news-skeleton"
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
        `}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            css={css`
              height: 80px;
              background: ${colors.snow};
              border: 0.5px solid ${colors.cloud};
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
              animation: pulse 1.5s ease-in-out infinite;

              @keyframes pulse {
                0%,
                100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.5;
                }
              }

              html[data-theme='dark'] & {
                background: #242424;
                border-color: rgba(255, 255, 255, 0.06);
                box-shadow: none;
              }
            `}
          />
        ))}
      </div>
    );
  }

  // Filter valid articles and limit
  const validArticles = (newsArticles || []).filter((article) => article?.id && article?.headline).slice(0, maxItems);

  // Empty state
  if (validArticles.length === 0) {
    return (
      <div
        css={css`
          ${fontStyles.mouse};
          color: ${colors.silver};
          text-align: center;
          padding: ${spacing.deci};
        `}
      >
        No recent news available
      </div>
    );
  }

  const handleCardClick = (article: PortfolioNewsArticle) => {
    // Navigate to chat with a prompt to explain the article
    const symbols =
      article.symbols && article.symbols.length > 0 ? article.symbols.slice(0, 3).join(', ') : 'the market';
    const message = `Explain this news for ${symbols}: ${article.headline}`;
    history.push('/chat', { message, from: 'markets' });
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.centi};
      `}
    >
      {validArticles.map((article) => (
        <div
          key={article.id}
          role="button"
          tabIndex={0}
          data-testid="market-news-item"
          onClick={() => handleCardClick(article)}
          onKeyDown={(e) => e.key === 'Enter' && handleCardClick(article)}
          css={css`
            background: ${colors.snow};
            border: 0.5px solid ${colors.cloud};
            border-radius: 8px;
            padding: ${spacing.deci};
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
              background-color: ${colors.fog};
            }

            html[data-theme='dark'] & {
              background: #242424;
              border-color: rgba(255, 255, 255, 0.06);
              box-shadow: none;

              &:hover {
                background-color: #2a2a2a;
              }
            }
          `}
        >
          {/* Content with optional image */}
          <div
            css={css`
              display: flex;
              gap: ${spacing.centi};
              margin-bottom: ${spacing.centi};
            `}
          >
            {/* Image (if available) */}
            {article.imageUrl && (
              <div
                css={css`
                  width: 64px;
                  height: 64px;
                  border-radius: 6px;
                  flex-shrink: 0;
                  overflow: hidden;
                  background: ${colors.cloud};

                  html[data-theme='dark'] & {
                    background: rgba(80, 78, 75, 0.3);
                  }
                `}
              >
                <img
                  src={article.imageUrl}
                  alt=""
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  `}
                />
              </div>
            )}

            {/* Text content */}
            <div
              css={css`
                flex: 1;
                min-width: 0;
                ${fontStyles.thinMouse};
                color: var(--text-color);
                line-height: 1.4;
              `}
            >
              {toSentenceCase(article.headline)}
            </div>
          </div>

          {/* Bottom row: badge + time */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.milli};
            `}
          >
            {(article.symbols && article.symbols.length > 0 ? article.symbols.slice(0, 3) : ['Market']).map(
              (symbol) => (
                <span
                  key={symbol}
                  css={css`
                    ${fontStyles.flea};
                    background: rgba(17, 131, 131, 0.1);
                    color: ${colors.petrolBlue};
                    padding: 2px 6px;
                    border-radius: 4px;

                    html[data-theme='dark'] & {
                      background: rgba(40, 181, 189, 0.15);
                      color: ${colors.skyBlue};
                    }
                  `}
                >
                  {symbol}
                </span>
              ),
            )}
            <span
              css={css`
                ${fontStyles.thinMouse};
                color: ${colors.silver};
              `}
            >
              {formatRelativeTime(safeParseDate(article.publishedAt))}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MarketNewsList;
